import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { textGray } from '../utils/colors';
import { blink } from '../utils/animations';

const TinyText = styled.p`
  font-size: 0.875rem;
  line-height: 1.4285em;
  letter-spacing: 0.5px;
  font-weight: 300;
  margin: 2px 0;
  color: ${textGray}
  transition: color 0.2s ease-in-out;

  > * {
    margin: 2px 0;
  }

  a {
    display: block;
    color: ${props => props.theme.tertiary};
    text-decoration: none;
    font-weight: 600;
    margin: 0.15rem 0;
    font-size: 0.875rem;
  }

  ul {
    margin: 0.4rem 0 0;
    padding: 0 0 0 1.2em;
  }

  ${props =>
    props.italic &&
    css`
      font-style: italic;
    `};

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;

      p {
        color: #ffffff;
      }
    `};

  ${props =>
    props.spacer &&
    css`
      margin-top: 0.75rem;
    `};
`;

const TinyTextWrapper = TinyText.withComponent('div');

const TinyTitle = TinyText.extend`
  text-transform: uppercase;
  line-height: 1.2857em;
  font-weight: 600;
  color: ${textGray}
    ${props =>
      props.inverted &&
      css`
        color: #ffffff;
      `};
`;

const RawTinyLink = TinyTitle.extend`
  text-decoration: none;
  ${props =>
    props.active &&
    css`
      color: ${props => props.theme.tertiary};
    `};

  &:hover {
    color: ${props => props.theme.quartenary};
  }
`;

const TinyLink = RawTinyLink.withComponent(({ active, inverted, ...rest }) => (
  <Link {...rest} />
));

const BaseLink = styled(({ active, inverted, ...rest }) => (
  <Link {...rest} />
))``;

const Line = styled.i`
  display: block;
  background-color: ${textGray};
  height: 1px;
  width: 2.125rem;
`;

const Lowercase = styled.span`
  text-transform: none;
`;

const Anchor = styled.a`
  color: ${props => props.theme.tertiary};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: ${textGray};
  }
`;

const List = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  order: 1;
`;

const MenuOverlay = styled.div`
  @media all and (min-width: 1024px) {
    ${props =>
      props.open &&
      css`
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background: rgba(255, 255, 255, 0.7);
        z-index: 1;
      `};
  }
`;

const Button = styled.button`
  background: ${props => props.theme.tertiary};
  display: inline-block;
  border: 0;
  padding: 0.5rem 1.2rem;
  font-size: 0.8125rem;
  color: #ffffff;
  cursor: pointer;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 1px;
  outline: none;
  border-radius: 4px;

  &:hover,
  &:focus {
    background: linear-gradient(
      41.68deg,
      ${props => props.theme.tertiary} 0%,
      ${props => props.theme.tertiary} 41.7%,
      #d9fdf8 100%
    );
  }

  ${props =>
    props.transparent &&
    css`
      color: ${props => props.theme.tertiary};
      background: transparent;
      border: 1px solid ${props => props.theme.tertiary};

      &:hover,
      &:focus {
        color: #ffffff;
      }
    `};
`;

const Container = styled.div`
  width: 73.125rem;
  margin: 0 auto;

  @media all and (max-width: 82.5rem) {
    width: 100vw;
    margin: 0;
    padding: 0 0 0 3.1rem;
  }

  @media screen and (max-width: 64rem) {
    padding: 0;
    width: auto;
  }

  @media all and (max-width: 40rem) {
    width: 100%;
    padding: 0;
  }
`;

const Row = styled.div`
  display: flex;
  color: #fff;
  padding: 0 0.75rem;
  position: relative;

  @media all and (max-width: 43.75rem) {
    flex-wrap: wrap;
  }
`;

const Column = styled.div`
  flex: 1;
  flex-basis: 50%;

  > * {
    margin: 1.3125rem 0.75rem;
  }

  ${props =>
    props.small &&
    css`
      flex-basis: 100px;
      min-width: 17.1875rem;
    `};

  @media all and (max-width: 43.75rem) {
    flex-basis: 100%;
  }

  ${props =>
    props.hugger &&
    css`
      > * {
        margin: 0;
      }
    `};
`;

const PageTitle = styled.h1`
  color: ${textGray};
  font-family: Source Sans Pro, sans-serif;
  font-size: 2.25rem;
  font-weight: 600;
  margin-top: 6rem;
  margin-bottom: 3rem;

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};
`;

const SecondaryTitle = styled.h2`
  color: ${textGray};
  font-family: Source Serif Pro, serif;
  font-size: 1.625rem;
  margin: 1.5rem 0;
  display: flex;
  align-items: center;

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};

  ${props =>
    props.highlight &&
    css`
      color: ${props.theme.tertiary};
    `} ${props =>
    props.thin &&
    css`
      font-weight: 300;
    `};
`;

const Card = styled.div`
  border-radius: 0.625rem;
  background-color: ${props => props.theme.quarternary};
  padding: 1px 0;

  ${props =>
    props.inverted &&
    css`
      background-color: #ffffff;
    `};
`;

const CardSpacing = styled.div`
  margin: 3rem 0;

  ${props =>
    props.hugger &&
    css`
      margin-bottom: 0;

      h2 {
        margin-bottom: 0.5rem;
      }
    `};
`;

const CardContent = styled.div`
  padding: 0 3.5rem;
  margin: 0;
`;

const CardActionStyle = CardContent.extend`
  display: block;
  margin: 1rem 0 0;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  background-color: rgba(255, 255, 255, 0.08);
  border-bottom-left-radius: 0.625rem;
  border-bottom-right-radius: 0.625rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.12s ease-in-out;
  position: relative;
  transition: opacity 0.2s ease-in-out;
  color: ${textGray};
  text-decoration: none;

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  ${props =>
    props.busy &&
    css`
      cursor: wait;
      animation: ${blink} 0.8s infinite ease-in-out;

      * {
        cursor: wait !important;
      }
    `};
`;

const CardAction = CardActionStyle.withComponent('a');

const CardActionIcon = styled.i`
  position: absolute;
  left: 1.5rem;
  top: 1.7rem;

  g {
    stroke: ${textGray};

    ${props =>
      props.inverted &&
      css`
        stroke: #ffffff;
      `};
  }
`;

const Edit = styled.a`
  margin-left: auto;
  cursor: pointer;
  display: flex;
  width: 21px;
  height: 21px;
  align-items: center;
  justify-content: center;

  svg {
    width: 21px;
    height: 21px;

    g {
      stroke: ${textGray};

      ${props =>
        props.inverted &&
        css`
          stroke: #ffffff;
        `};
    }
  }

  .cross {
    width: 16px;
    height: 16px;

    g {
      stroke: ${textGray};

      ${props =>
        props.inverted &&
        css`
          stroke: #ffffff;
        `};
    }
  }

  .cls-1 {
    stroke: #fff;
    fill: transparent;
  }
`;

export {
  Container,
  TinyText,
  TinyTextWrapper,
  TinyTitle,
  RawTinyLink,
  TinyLink,
  Line,
  List,
  Lowercase,
  Anchor,
  BaseLink,
  MenuOverlay,
  Button,
  Row,
  Column,
  PageTitle,
  SecondaryTitle,
  Card,
  CardSpacing,
  CardContent,
  CardAction,
  CardActionIcon,
  Edit
};



// WEBPACK FOOTER //
// ./src/components/CommonUiComponents.js